/**
 * RuleAnalytics — performance KPIs computed from recent_runs data.
 * No new API call needed — uses the runs already in the detail response.
 */

interface RuleRun {
  id: number;
  ran_at: string;
  rows_scanned: number;
  exceptions_found: number;
  execution_time_ms: number;
  status: string;
}

interface RuleAnalyticsProps {
  runs: RuleRun[];
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function RuleAnalytics({ runs }: RuleAnalyticsProps) {
  if (!runs || runs.length === 0) return null;

  const totalRuns = runs.length;
  const totalScanned = runs.reduce((s, r) => s + r.rows_scanned, 0);
  const totalFound = runs.reduce((s, r) => s + r.exceptions_found, 0);
  const hitRate = totalScanned > 0 ? ((totalFound / totalScanned) * 100).toFixed(1) : '0.0';
  const avgTime = Math.round(runs.reduce((s, r) => s + (r.execution_time_ms || 0), 0) / totalRuns);
  const lastRun = runs[0]?.ran_at;

  const kpis = [
    { label: 'Total Runs', value: String(totalRuns) },
    { label: 'Hit Rate', value: `${hitRate}%` },
    { label: 'Avg Time', value: `${avgTime}ms` },
    { label: 'Last Run', value: lastRun ? timeAgo(lastRun) : 'Never' },
  ];

  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Rule Performance</h4>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
        {kpis.map(k => (
          <div key={k.label} style={{
            background: 'var(--bg-card)',
            borderRadius: 6,
            padding: '10px 12px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              {k.label}
            </div>
            <div className="mono" style={{ fontSize: 16, fontWeight: 600 }}>
              {k.value}
            </div>
          </div>
        ))}
      </div>

      {/* Runs table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-default)', color: 'var(--text-tertiary)', fontSize: 10, textTransform: 'uppercase' as const }}>
            <th style={{ padding: '6px 8px', textAlign: 'left' }}>Date</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Scanned</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Matched</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Time</th>
            <th style={{ padding: '6px 8px', textAlign: 'center' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {runs.map(run => (
            <tr key={run.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              <td style={{ padding: '6px 8px' }}>{new Date(run.ran_at).toLocaleDateString()}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{run.rows_scanned.toLocaleString()}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{run.exceptions_found.toLocaleString()}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{run.execution_time_ms}ms</td>
              <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                <span style={{ color: run.status === 'SUCCESS' ? 'var(--green)' : 'var(--red)', fontWeight: 600, fontSize: 11 }}>
                  {run.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
